import React, { useState, useEffect } from "react";
import { useLocation, NavLink, Link } from "react-router-dom";
import { BookOpen, Flame, Rocket } from "lucide-react";
import CTALarge from "../CTA/CTALarge.jsx";
import CTASmall from "../CTA/CTASmall.jsx";
import SearchBar from "../ui/SearchBar.jsx";
// import ThemeToggle from "../Nav/ThemeToggle.jsx";
import pageConfig from "../../config/pageConfig.js";
import guidesConfig from "../../config/guidesConfig.js";
import "./Sidebar.css";
import "../GuidesMain/nav-pages.css";

import { sidebarCategories } from "./SidebarData.js";
import { sidebarGuideCategories } from "./SidebarGuides.js";

const Sidebar = () => {
  const location = useLocation();

  // Always call hooks first
  // Responsive detection: screen is small if width <1050px.
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 1050);
  useEffect(() => {
    const handleResize = () => setIsSmallScreen(window.innerWidth < 1050);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const cta =
    <CTALarge
      to="/get-started/step1"
      text="Understand Matched Betting in 180 Seconds"
      icon={Rocket}
    />

  const ctaSmall =
    location.pathname === "/" ? (
      <CTASmall
        to="/get-started/step1"
        text="Understand Matched Betting in 180 Seconds"
        icon={Rocket}
      />
    ) : null;

  // Toggle for full category list (desktop only)
  const getDefaultTab = () =>
    location.pathname.startsWith("/calculators") ? "calculators" : "guides";
  const [activeTab, setActiveTab] = useState(getDefaultTab());
  useEffect(() => {
    if (!isSmallScreen) {
      if (location.pathname.startsWith("/calculators")) {
        setActiveTab("calculators");
      } else {
        setActiveTab("guides");
      }
    }
  }, [location.pathname, isSmallScreen]);

  // Mobile menu open/close state.
  const [menuOpen, setMenuOpen] = useState(false);
  const toggleMenu = () => setMenuOpen(!menuOpen);
  // Closes the sidebar when a navigation link is clicked.
  const handleNavClick = () => setMenuOpen(false);

  function getInsensitiveConfig(key, configObject) {
    const lowerKey = key.toLowerCase();
    for (const entryKey in configObject) {
      if (
        Object.prototype.hasOwnProperty.call(configObject, entryKey) &&
        entryKey.toLowerCase() === lowerKey
      ) {
        return configObject[entryKey];
      }
    }
    return undefined;
  }

  // Default keys to use if no similar guides are defined on currentGuide.
  const defaultGuideKeys = ["matchedBettingGuide", "FreeBetsGuide", "HowToReadOddsGuide"];

  // Find the current guide based on the location.
  const currentGuide = Object.values(guidesConfig).find(
    (guide) => guide.route === location.pathname
  ) ?  Object.values(guidesConfig).find(
    (guide) => guide.route === location.pathname
  ) : Object.values(pageConfig).find(
    (calc) => calc.route === location.pathname
  )

  console.log('curr', currentGuide);

  let relatedGuides =
    currentGuide && currentGuide.similarGuides
      ? currentGuide.similarGuides.map((guideKey) => {
          const config = getInsensitiveConfig(guideKey, guidesConfig);
          if (!config) {
            console.warn(`Warning: guidesConfig key "${guideKey}" is undefined.`);
          }
          return config;
        }).filter(Boolean) // remove any undefined entries
      : defaultGuideKeys.map((guideKey) => {
          const config = getInsensitiveConfig(guideKey, guidesConfig);
          if (!config) {
            console.warn(`Warning: guidesConfig key "${guideKey}" is undefined.`);
          }
          return config;
        }).filter(Boolean);

  // Sort so that guides with highlight === true come first.
  relatedGuides.sort((a, b) => {
    if (a.highlight === true && b.highlight !== true) return -1;
    if (a.highlight !== true && b.highlight === true) return 1;
    return 0;
  });

  const currentCalc = Object.values(pageConfig).find(
    (calc) => calc.route === location.pathname
  ) ?  Object.values(pageConfig).find(
    (calc) => calc.route === location.pathname
  ) : Object.values(guidesConfig).find(
    (guide) => guide.route === location.pathname
  )

  const relatedCalcs =
    currentCalc && currentCalc.similar
      ? currentCalc.similar.map((calcKey) => {
          const config = getInsensitiveConfig(calcKey, pageConfig);
          if (!config) {
            console.warn(`Warning: pageConfig key "${calcKey}" is undefined.`);
          }
          return config;
        }).filter(Boolean)
      : ["layStakeCalculator", "riskFreeBetCalculator", "matchPickerCalculator"].map(
          (calcKey) => {
            const config = getInsensitiveConfig(calcKey, pageConfig);
            if (!config) {
              console.warn(`Warning: pageConfig key "${calcKey}" is undefined.`);
            }
            return config;
          }
        ).filter(Boolean);
  // For desktop toggled content: full list categories.
  const categoriesToShow =
    activeTab === "calculators" ? sidebarCategories : sidebarGuideCategories;

  // Do not render the sidebar on /get-started pages.
  if (location.pathname.startsWith("/get-started")) {
    return null;
  }
  if (isSmallScreen || (location.pathname ==="/" && isSmallScreen)) {
    // MOBILE LAYOUT:
    return (
      <>
        <div className="mobile-header">
          {/* <div className="theme-toggle-container">
            <ThemeToggle />
          </div> */}
          <button className="hamburger" onClick={toggleMenu}>
            {menuOpen ? "✖" : "☰"}
          </button>
        </div>
        <aside className={`sidebar ${menuOpen ? "active" : ""}`}>
          {/* Render SearchBar with autoClose enabled and pass the onResultClick callback */}
          <SearchBar autoClose={true} onResultClick={handleNavClick} />
          {/* Toggle bar as NavLinks */}
          <div className="toggle-bar">
            <NavLink
              to="/guides"
              className={`toggle-btn ${location.pathname.startsWith("/guides") ? "active" : ""}`}
              onClick={handleNavClick}
            >
              Guides
            </NavLink>
            <NavLink
              to="/calculators"
              className={`toggle-btn ${location.pathname.startsWith("/calculators") ? "active" : ""}`}
              onClick={handleNavClick}
            >
              Calculators
            </NavLink>
          </div>
          {/* CTA always shows on mobile */}
          {cta}
          {/* Always render the related sections */}
          <div className="category-section">
            <h2 className="category-title">Related Calculators</h2>
            <div className="calculator-grid">
              {relatedCalcs.map((calc, idx) => {
                if (!calc || !calc.route) {
                  console.warn(`Missing route for related calc at index ${idx}`, calc);
                  return null;
                }
                return (
                  <NavLink
                    key={idx}
                    onClick={handleNavClick}
                    to={calc.route}
                    className="grid-item"
                  >
                    <div className="icon">{calc.sidebar.icon}</div>
                    <div className="label">{calc.sidebar.label}</div>
                    {calc.sidebar.sub && <div className="sub">{calc.sidebar.sub}</div>}
                  </NavLink>
                );
              })}
            </div>
          </div>
          <div className="category-section">
            <h2 className="category-title">Related Guides</h2>
            <div className="related-list">
              {relatedGuides.map((guide, idx) => (
                <Link
                  key={idx}
                  to={guide.route}
                  className={`guide-link ${guide.highlight ? "guide-link--highlight" : ""}`}
                  onClick={handleNavClick}
                >
                  <div className="guide-main">
                    <div className="guide-icon">{guide.sidebar.icon || <BookOpen size={16} />}</div>
                    <div className="guide-label">{guide.sidebar.label}</div>
                  </div>
                  {guide.highlight && (
                    <div className="guide-highlight">
                      <Flame size={16} color="#fff" />
                    </div>
                  )}
                </Link>
              ))}
            </div>
          </div>
        </aside>
      </>
    );
  } else if (location.pathname !== "/"){
    // DESKTOP LAYOUT:
    return (
      <>
        <aside className="sidebar">
          {ctaSmall}
          <div className="category-section">
            <h2 className="category-title">Related Calculators</h2>
            <div className="calculator-grid">
              {relatedCalcs.map((calc, idx) => {
                if (!calc || !calc.route) {
                  console.warn(`Missing route for related calc at index ${idx}`, calc);
                  return null;
                }
                return (
                  <NavLink
                    key={idx}
                    onClick={handleNavClick}
                    to={calc.route}
                    className="grid-item"
                  >
                    <div className="icon">{calc.sidebar.icon}</div>
                    <div className="label">{calc.sidebar.label}</div>
                    {calc.sidebar.sub && <div className="sub">{calc.sidebar.sub}</div>}
                  </NavLink>
                );
              })}
            </div>
          </div>
          <div className="category-section">
            <h2 className="category-title">Related Guides</h2>
            <div className="related-list">
              {relatedGuides.map((guide, idx) => (
                <Link
                  key={idx}
                  to={guide.route}
                  className={`guide-link ${guide.highlight ? "guide-link--highlight" : ""}`}
                  onClick={handleNavClick}
                >
                  <div className="guide-main">
                    <div className="guide-icon">
                      {guide.sidebar.icon || <BookOpen size={16} />}
                    </div>
                    <div className="guide-label">{guide.sidebar.label}</div>
                  </div>
                  {guide.highlight && (
                    <div className="guide-highlight">
                      <Flame size={16} color="#fff" />
                    </div>
                  )}
                </Link>
              ))}
            </div>
          </div>
          <div className="toggle-bar">
            <button
              className={`toggle-btn ${activeTab === "guides" ? "active" : ""}`}
              onClick={() => setActiveTab("guides")}
            >
              Guides
            </button>
            <button
              className={`toggle-btn ${activeTab === "calculators" ? "active" : ""}`}
              onClick={() => setActiveTab("calculators")}
            >
              Calculators
            </button>
          </div>
          {categoriesToShow.map((category, catIdx) => (
            <div key={catIdx} className="category-section">
              <h2 className="category-title">{category.title}</h2>
              <div className={activeTab === "guides" ? "guide-list" : "calculator-grid"}>
                {category.items
                  .filter((item) => item.link !== "*" && item?.display !== false)
                  .map((item, idx) => {
                    return activeTab === "guides" ? (
                      <NavLink
                        key={idx}
                        onClick={handleNavClick}
                        to={item.link}
                        className="guide-link"
                      >
                        <div className="guide-icon">
                          <BookOpen size={16} />
                        </div>
                        <div className="guide-label">{item.label}</div>
                      </NavLink>
                    ) : (
                      <NavLink
                        key={idx}
                        onClick={handleNavClick}
                        to={item.link}
                        className="grid-item"
                      >
                        <div className="icon">{item.icon}</div>
                        <div className="label">{item.label}</div>
                        {item.sub && <div className="sub">{item.sub}</div>}
                      </NavLink>
                    );
                  })}
              </div>
            </div>
          ))}
        </aside>
      </>
    );
  }
};

export default Sidebar;
